/**
 * AdminShell — Batch 27f port to the prototype aesthetic.
 *
 * Visual contract (see /Branding/prototype.jpg):
 *   - Dark-by-default canvas (`bg-surface-canvas`); light theme is opt-in via
 *     ThemeContext + the header toggle.
 *   - Wordmark (`<Wordmark/>`) top-left of a near-black sidebar.
 *   - Sidebar grouped into eyebrow sections (`WORKSPACE` / `ADMIN OPS`).
 *   - The personal account affordance lives top-right (the shared
 *     `<AccountMenu/>`, reused from the customer AppShell — P2 SA console
 *     consolidation 2026-06-15). The former bottom-left "Settings" sidebar item
 *     (which linked to the same `/profile`) was removed as redundant.
 *   - Top bar shows the user's first + last name (founder ask: never the
 *     role string), avatar gradient initial, and the account dropdown
 *     (identity + theme toggle + Profile + Sign out).
 *   - Card depth on the main panel: subtle border + shadow on both surfaces.
 */
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Outlet, NavLink } from "react-router-dom";
import { AccountMenu } from "@/components/layout/AccountMenu";
import { ImpersonationBanner } from "@/components/common/ImpersonationBanner";
import { Toast } from "@/components/common/Toast";
import { Modal } from "@/components/common/Modal";
import { Wordmark } from "@/components/common/Wordmark";
import { useAuthStore } from "@/stores/useAuthStore";

export function AdminShell(): JSX.Element {
  // RBAC FE WS-D invariant #8 — the super-admin console nav link must be HIDDEN
  // while impersonating a tenant user, via an explicit impersonation check (not
  // merely by SA-ness). The backend `require_not_impersonating` is the real
  // boundary; hiding the link only prevents dead-403 clicks + an SA-identity
  // leak on a tenant-scoped session.
  const impersonating = useAuthStore((s) => s.impersonation) !== null;

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `relative flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
      isActive
        ? "bg-brand-100 text-text-primary before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-r before:bg-accent-secondary dark:bg-white/[0.06] dark:text-white"
        : "text-text-secondary hover:bg-brand-100/60 dark:text-brand-100/70 dark:hover:bg-white/[0.04] dark:hover:text-white"
    }`;

  return (
    <div className="flex h-full flex-col bg-surface-canvas text-text-primary dark:bg-surface-canvas dark:text-text-primary">
      <ImpersonationBanner />
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="flex w-sidebar-w shrink-0 flex-col border-r border-[var(--p-slate-150)] bg-white dark:border-white/[0.06] dark:bg-surface-app-chrome">
          {/* Logo treatment (founder ruling 2026-06-08): the SA wordmark sits on
              an always-dark brand chip so the off-white rendition is legible in
              BOTH themes (in light mode the rest of this sidebar is white, so a
              forced off-white logo would otherwise wash out). #080d18 = the dark
              app-chrome token, pinned theme-independent. */}
          <div className="flex h-16 items-center bg-brand-950 px-5">
            <Wordmark height={22} surface="dark" />
          </div>
          <nav className="flex-1 space-y-5 px-3 py-2">
            <SidebarSection label="Workspace">
              <NavLink to="/dashboard" className={navLinkClass} end>
                Dashboard
              </NavLink>
              <NavLink to="/tenants" className={navLinkClass}>
                Tenants
              </NavLink>
            </SidebarSection>
            {/* SA GLOBAL SETTINGS (P1, 2026-06-15): platform-wide config,
                distinct from per-tenant settings. Collapsible to reduce clutter
                (founder req). Holds the Unavatar PRO token + the existing Bias
                guardrails (moved here from Workspace; route unchanged). */}
            <SidebarSection label="Global settings" collapsible defaultCollapsed>
              <NavLink to="/global-settings/unavatar" className={navLinkClass}>
                Unavatar token
              </NavLink>
              <NavLink to="/bias-guardrails" className={navLinkClass}>
                Bias guardrails
              </NavLink>
            </SidebarSection>
            <SidebarSection label="Admin ops">
              <NavLink to="/triage" className={navLinkClass}>
                Triage
              </NavLink>
              <NavLink to="/integrations" className={navLinkClass}>
                Integrations
              </NavLink>
              <NavLink to="/platform-health" className={navLinkClass}>
                Health
              </NavLink>
              {/* RBAC FE WS-D #8 — hidden while impersonating (route is also a
                  no-op under impersonation; backend require_not_impersonating
                  is the real boundary). */}
              {impersonating ? null : (
                <NavLink to="/super-admins" className={navLinkClass}>
                  Super admins
                </NavLink>
              )}
              <NavLink to="/demos" className={navLinkClass}>
                Demos
              </NavLink>
              {/* Legacy /demo route stays active for direct-link backwards compat */}
            </SidebarSection>
          </nav>
        </aside>

        {/* Main content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <header className="flex h-16 items-center justify-between border-b border-[var(--p-slate-150)] bg-white px-6 dark:border-white/[0.06] dark:bg-surface-app-chrome">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-text-secondary dark:text-brand-100/50">
              Admin Console
            </p>
            {/* Personal account control — reused from the customer AppShell
                (P2 SA console consolidation 2026-06-15). Identity + theme
                toggle + Profile + Sign out, anchored top-right. The SA host has
                no tenant slug, so Profile points at `/profile`. The Settings
                link was removed from the dropdown in feat/tenant-profile-ia —
                no settingsTo prop needed. The dropdown carries the #275 `z-50`
                fix from the shared component, so it floats above page content. */}
            <AccountMenu profileTo="/profile" />
          </header>
          <main className="flex-1 overflow-y-auto px-[22px] py-5">
            <Outlet />
          </main>
        </div>
      </div>

      <Toast />
      <Modal />
    </div>
  );
}

function SidebarSection({
  label,
  children,
  collapsible = false,
  defaultCollapsed = false,
}: {
  label: string;
  children: React.ReactNode;
  /** When true, the section header is a toggle button and the body collapses. */
  collapsible?: boolean;
  /** Initial collapsed state (only meaningful when `collapsible`). */
  defaultCollapsed?: boolean;
}): JSX.Element {
  const [collapsed, setCollapsed] = useState(collapsible && defaultCollapsed);

  const labelClass =
    "px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-brand-500/70 dark:text-brand-100/40";

  if (!collapsible) {
    return (
      <div>
        <p className={labelClass}>{label}</p>
        <div className="space-y-0.5">{children}</div>
      </div>
    );
  }

  const sectionId = `sidebar-section-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div>
      <button
        type="button"
        aria-expanded={!collapsed}
        aria-controls={sectionId}
        onClick={() => setCollapsed((v) => !v)}
        className={`flex w-full items-center justify-between ${labelClass} hover:text-brand-500 dark:hover:text-brand-100/70`}
      >
        <span>{label}</span>
        <ChevronDown
          className={`h-3 w-3 transition-transform ${collapsed ? "" : "rotate-180"}`}
          aria-hidden="true"
        />
      </button>
      {collapsed ? null : (
        <div id={sectionId} className="space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
}
